import React, { useState, useEffect, useRef, ReactNode } from 'react';

import { motion } from 'framer-motion';
import ShimmerViewMore from './shimmer';
import Icon, { IconName } from '@utilities/icon';

interface ViewMoreProps {
  icon?: IconName;
  title?: string;
  content?: ReactNode;
  children?: ReactNode;
  isLoading?: boolean;
  collapsedHeight?: number;
  overflowThreshold?: number;
  showMoreLabel?: string;
  showLessLabel?: string;
  className?: string;
}

const DEFAULT_COLLAPSED_HEIGHT = 90;
const DEFAULT_OVERFLOW_THRESHOLD = 100;

const ViewMore = ({
  icon,
  title,
  content,
  children,
  isLoading = false,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  overflowThreshold = DEFAULT_OVERFLOW_THRESHOLD,
  showMoreLabel = 'نمایش بیشتر',
  showLessLabel = 'نمایش کمتر',
  className = 'px-4',
}: ViewMoreProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const textRef = useRef<HTMLDivElement | null>(null);

  const body = children ?? content;

  useEffect(() => {
    if (textRef.current) {
      const naturalHeight = textRef.current.scrollHeight;
      setContentHeight(naturalHeight);
      setIsOverflowing(naturalHeight > overflowThreshold);
    }
  }, [body, overflowThreshold]);

  if (isLoading) return <ShimmerViewMore />;

  return (
    <div className={`flex flex-col gap-y-2 ${className}`}>
      <motion.div
        className="flex flex-col gap-y-4 overflow-hidden"
        initial={false}
        animate={{ height: isOpen ? contentHeight : collapsedHeight }}
        transition={{ duration: 0.3 }}
      >
        <div ref={textRef} className="flex flex-col gap-y-4 pb-2">
          {title && (
            <div className="flex items-center gap-x-2 justify-start">
              {icon && <Icon name={icon} width={1} height={1} />}
              <span className="text-Label/Medium text-content-primary">
                {title}
              </span>
            </div>
          )}

          <div className="text-Body/xSmall text-content-secondary mb-2">
            {body}
          </div>
        </div>
      </motion.div>

      {isOverflowing && (
        <div className="flex justify-center pb-2">
          <button
            className="text-Body/Small text-content-primary font-semibold text-center"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
          >
            <div className="flex gap-x-1 items-center">
              <span>{isOpen ? showLessLabel : showMoreLabel}</span>
              <Icon
                name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                width={1}
                height={1}
              />
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewMore;
